<template>
  <div class="f-h gemba-walks" :class="elClasses">

    <FiltersZone :modal-title="$t('message.OPTIONS')">
      <ComponentsList :value="completedGwOptions" @edit="paramsEdit" />
    </FiltersZone>

    <div class="f-h-below-filters">

      <!-- List of GW -->
      <RefreshOnscrollList
        ref="refList"
        class="gemba-walks__list"
        @scrollBottom="refresh(true)"
      >
        <div>
          <div class="gemba-walks__col-headers v-centerer">
            <SortOption class="gemba-walks__col-headers__gw" :label="$t('message.GW')" />
            <SortOption
              class="gemba-walks__col-headers__date"
              :label="$t('message.CREATION_DATE')"
              :is-current="completedGwOptSortBy.value === 0"
              :is-desc="completedGwOptSortBy.value === 0 && completedGwOptSortBy.desc"
              sortable
              @click.native="clickedSortOption(0)"
            />
            <SortOption class="gemba-walks__col-headers__leader" :label="$t('message.LEADER')" />
            <SortOption class="gemba-walks__col-headers__participants" :label="$t('message.PARTICIPANTS')" />
            <SortOption class="gemba-walks__col-headers__delete" :label="$t('message.DELETE')" />
          </div>

          <GwCompletedItem
            v-for="gw in sortedGWs"
            ref="gwItems"
            :key="gw.id"
            :gw="gw"
            @delete="deleteGW(gw)"
            @toggleModal="toggleModal($event, gw)"
          />
        </div>
      </RefreshOnscrollList>
      <!-- End of List of GW -->
      <LoadingOverlay v-model="loading" />
    </div>
    <GwTeamRecapModal
      v-if="modalContent"
      :modal-content="modalContent"
      :visible="participatingTeamsModal"
      @toggleModal="toggleModal()"
    />
  </div>
</template>

<script>
/**
 * Page for a list of completed field tours (gemba walks)
 * This page uses the RefreshOnscrollList for pagination (see server/gembaWalks/get.php)
 */

import Vue from 'vue';
import $ from 'jquery';
import toastr from 'toastr';
import { isEqual } from 'lodash';
// Vuex utils
import { createNamespacedHelpers } from 'vuex';
import { FETCH_GW_TEMPLATES } from '@/store/modules/gemba-walks/action-types';
// Components
import ComponentsList from '@/components/components-list.vue';
import FiltersZone from '@/components/filters-zone.vue';
import GwCompletedItem from '@/components/gw-completed-item.vue';
import GwTeamRecapModal from '@/components/gw-team-recap-modal.vue';
import LoadingOverlay from '@/components/loading-overlay.vue';
import RefreshOnscrollList from '@/components/refresh-onscroll-list.vue';
import SortOption from '@/components/sort-option.vue';
// Mixins
import mixinCompletedGwOptions from '@/mixins/page-options/gemba-walks.vue';
// Utils
import IAHelpStep from '@/utils/classes/IAHelpStep';
import { itemWidthClasses } from '@/utils/main';


const { mapActions: gwMapActions } = createNamespacedHelpers('gembaWalks');
const GW_REQ_LIMIT = 30;

export default {

  name: 'GembaWalks',

  components: {
    ComponentsList,
    FiltersZone,
    GwCompletedItem,
    GwTeamRecapModal,
    LoadingOverlay,
    RefreshOnscrollList,
    SortOption,
  },

  mixins: [mixinCompletedGwOptions],

  data() {
    return {
      modalContent: null,
      participatingTeamsModal: false,
      // Filtering
      demoTmp: [],
      whichItems: [
        { id: 0, label: this.$t('message.GW_BY_MY_TEAM') },
        { id: 1, label: this.$t('message.GW_FOR_MY_TEAM') },
      ],
      // Data
      loading: false,
      gembaWalks: [],
      demoGW: [],
      sqlLimit: GW_REQ_LIMIT,
      itemsLimitOffset: 0,
      refreshTimeout: null,
      elClasses: {},
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.GW_COMPLETE'),
    };
  },

  computed: {
    sortedGWs() {
      // Filter
      let gws;
      if (this.templateFilterAllowed) {
        gws = this.compGW.filter(gw => this.completedGwOptSelectedTemplates.indexOf(gw.template) > -1);
      } else {
        gws = this.compGW;
      }
      // Sort
      const { desc } = this.completedGwOptSortBy;
      switch (this.completedGwOptSortBy.value) {
        case 0: return !desc
          ? gws.sort((a, b) => (a.mDate.isAfter(b.mDate) ? 1 : -1))
          : gws.sort((a, b) => (b.mDate.isAfter(a.mDate) ? 1 : -1));

        default: return gws;
      }
    },
    templateFilterAllowed() {
      return this.completedGwOptWhich === 0;
    },
    compGW() {
      return this.demoMode ? this.demoGW : this.gembaWalks;
    },
    /** Array: All accessible templates */
    gwTemplates() {
      const { templatesPerTeam } = this.$store.state.gembaWalks;
      return this.completedGwOptAccessibleTeams.flatMap(team => templatesPerTeam[`${team.id}`] || []);
    },
    /** Array: To trigger the refresh watcher */
    querySelectedTemplateIds() {
      if (this.$route.query.completedGwTemplateIds) {
        const tIds = this.$route.query.completedGwTemplateIds.split(',').map(id => Number(id));
        return tIds;
      }
      return [];
    },
    // ============
    // Query params
    // ============
    completedGwOptSelectedTemplates() {
      const ids = this.$route.query.completedGwTemplateIds;
      if (ids === '') {
        return [];
      }
      if (ids) {
        const tIds = ids.split(',').map(id => Number(id));
        return this.gwTemplates.filter(t => tIds.includes(t.id)).map(t => t.id);
      }
      return this.gwTemplates.map(t => t.id);
    },
    /** Object: Sort by options */
    completedGwOptSortBy() {
      const sb = { value: 0, desc: true };
      sb.value = typeof this.$route.query.sortBy === 'undefined' ? 0 : Number(this.$route.query.sortBy);
      sb.desc = this.$route.query.sortDesc !== 'false';
      return sb;
    },
    /** Filter: Choose if user is viewing GW for his team or those his team  */
    completedGwOptWhich() {
      const items = typeof this.$route.query.whichItemsChoice === 'undefined'
        ? 0
        : Number(this.$route.query.whichItemsChoice);
      return items;
    },
  },

  watch: {
    querySelectedTemplateIds(newVal, oldVal) {
      if (!isEqual(newVal, oldVal)) {
        this.$nextTick(this.refresh);
      }
    },
    completedGwOptSortBy(newVal, oldVal) {
      if (!isEqual(newVal, oldVal)) {
        this.$nextTick(this.refresh);
      }
    },
    completedGwOptWhich(newVal, oldVal) {
      if (!isEqual(newVal, oldVal)) {
        this.$nextTick(this.refresh);
      }
    },
  },

  created() {
    this.FETCH_GW_TEMPLATES().then(this.refresh);
    $(window).on(`resize.${this.uuid}`, this.onSizeChanged);
  },

  mounted() {
    this.onSizeChanged();
  },

  beforeDestroy() {
    $(window).off(`resize.${this.uuid}`);
    clearTimeout(this.refreshTimeout);
  },

  methods: {
    /** A column header has been clicked to sort the list of gws */
    clickedSortOption(optionIdx) {
      const option = { key: 'sortBy', value: { value: optionIdx, desc: true } };
      if (this.completedGwOptSortBy.value === optionIdx) {
        option.value.desc = !this.completedGwOptSortBy.desc;
      }
      this.paramsEdit(option);
    },
    toggleModal(event, gw) {
      if (this.participatingTeamsModal) {
        this.modalContent = null;
      } else {
        this.modalContent = { data: event, gw };
      }
      this.participatingTeamsModal = !this.participatingTeamsModal;
    },
    refresh(increaseLimit) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = setTimeout(() => {
        this.doRefresh(increaseLimit);
      }, 150);
    },
    doRefresh(increaseLimit) {
      if (increaseLimit !== true) {
        this.sqlLimit = GW_REQ_LIMIT;
        this.itemsLimitOffset = 0;
      }
      const params = {
        completed: true,
        audited: (this.completedGwOptWhich === 1),
        templates: this.completedGwOptWhich === 0 ? this.completedGwOptSelectedTemplates : 0,
        sortBy: { index: this.completedGwOptSortBy.value, desc: this.completedGwOptSortBy.desc },
        limit: { offset: this.itemsLimitOffset, count: this.sqlLimit },
      };

      // Fetch data
      // TODO: Manage incremental loading through Vuex
      this.loading = true;
      Vue.axios({
        method: 'post',
        url: '/server/gembaWalks/get.php',
        data: params,
      })
        .then(({ data }) => {
          if (Array.isArray(data)) {
            if (increaseLimit !== true) {
              this.gembaWalks = [];
            }
            this.itemsLimitOffset += data.length;
            this.gembaWalks.push(...data.map(gw => ({ ...gw, mDate: this.$moment(gw.date) })));
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    /** Replace GET parameters in query when componentsList is edited */
    paramsEdit(option) {
      const query = Object.assign({}, this.$route.query);
      if (option.key === 'sortBy') {
        query.sortBy = option.value.value;
        query.sortDesc = option.value.desc ? 'true' : 'false';
      } else if (option.key === 'which') {
        query.whichItemsChoice = option.value;
      } else if (option.key === 'templates') {
        query.completedGwTemplateIds = option.value.join(',');
      }
      if (!isEqual(query, this.$route.query)) {
        this.$router.replace({ query });
      }
    },
    /** Template deletion */
    deleteGW(gw) {
      this.App.post('/server/gembaWalks/delete.php', { id: gw.id }, (d) => {
        if (d === 1) {
          toastr.success(this.$t('message.GW_ITEM_DELETED'));
          this.refresh();
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Style of the GW items is determined by the size of $el. Apply classes */
    onSizeChanged(callOnChildren) {
      const { refList, gwItems } = this.$refs;
      const elt = (gwItems && gwItems[0] && gwItems[0].$el) || refList.$el;
      const width = elt.offsetWidth;
      this.elClasses = itemWidthClasses
        .reduce((classes, wc) => ({ ...classes, [wc.className]: width <= wc.width }), {});

      if (callOnChildren) {
        gwItems.forEach((comp) => {
          if (comp && comp.onSizeChanged) {
            comp.onSizeChanged();
          }
        });
      }
    },
    createHelpSteps() {
      this.makeDemoValues();
      return [
        new IAHelpStep(null, this.$t('help.GW_DONE_INTRO')),
        new IAHelpStep('.gw-item', this.$t('help.GW_DONE_ITEM')),
        new IAHelpStep('.gw-item__delete', this.$t('help.GW_DONE_DELETE'), (() => {
          $('.left-drawer-panel').addClass('display-no-transition');
        })),
        new IAHelpStep('.left-drawer-panel', this.$t('help.GW_DONE_SORT_FILTER'), (() => {}), [
          {
            text: this.$t('help.GW_DONE_TMP_LINK'),
            action() {
              window.open(this.$router.resolve({ path: '/gw-templates' }).href, '_blank');
            },
          },
        ]),
        new IAHelpStep(this.$refs.whichItems, this.$t('help.GW_DONE_DISPLAY'), (() => {
          $('.left-drawer-panel').removeClass('display-no-transition');
        })),
      ];
    },
    makeDemoValues() {
      const mf = 'YYYY-MM-DD HH:mm:ss';
      const sortableFormat = 'YYYYMMDDHHmmss';
      const now = this.$moment();
      const yest = this.$moment(now).subtract(1, 'd');
      this.demoGW = [
        {
          createdSortable: now.format(sortableFormat),
          date: now.format(mf),
          duration: '1120',
          id: '6',
          name: this.$t('help.DEMO_EVENT3'),
          template: '1',
        },
        {
          createdSortable: yest.format(sortableFormat),
          date: yest.format(mf),
          duration: '1020',
          id: '5',
          name: this.$t('help.DEMO_EVENT3'),
          template: '1',
        },
      ];

      this.demoTmp = [{
        selected: true,
        date: null,
        duration: '1000',
        id: '1',
        name: this.$t('help.DEMO_EVENT3'),
        template: '0',
      }];
    },
    ...gwMapActions({
      FETCH_GW_TEMPLATES,
    }),
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.gemba-walks {
  .f-h-below-filters {
    overflow: auto;
    padding: 0px;
  }

  .refresh-onscroll-list {
    padding: 0 $page-content-padding;
  }

  &__col-headers {
    margin-top: 0.8em;
    height: 1.8em;

    & &__gw {
      flex: 1;
      justify-content: unset;
    }

    &__leader {
      flex: 0 0 9.5rem;
    }

    &__date {
      // Width = 6em + margins of 4rem
      flex: 0 0 10rem;
      text-align: left;
    }

    &__participants, &__delete {
      flex: 0 0 5.72rem;
    }
  }

  &__list {
    height: calc(100% - 2.6rem);
  }

  &.sm-landscape-size {
    .gemba-walks__col-headers {
      &__date {
        flex: 0 0 7rem;
      }
      &__participants, &__delete {
        flex: 0 0 3.72rem;
      }
    }
  }

  &.sm-portrait-size {
    .gemba-walks__col-headers {
      &__leader {
        display: none;
      }
    }
  }
}

</style>
